import { Estimate, EstimateStatus } from '../types';

/**
 * Propuestas hechas para un PROSPECTO, antes de que el cliente exista.
 *
 * El estado de una de estas propuestas se lee en un solo campo: mientras
 * `sourceLeadId` tenga valor, la propuesta cuelga del prospecto y su `clientId`
 * va vacío. En el momento en que el trato se gana, el prospecto pasa a cliente,
 * la propuesta recibe `clientId`/`clientName` y `sourceLeadId` vuelve a null
 * (services/database.ts → attachLeadWorkToClient).
 *
 * Que sea UN campo y no dos —una bandera más el id— es lo que impide el estado
 * imposible de "ya tiene cliente pero sigue marcada como del prospecto", y lo
 * que hace que el barrido de abajo no pueda convertir dos veces al mismo.
 */

/**
 * Estados en los que un prospecto ya dejó de ser prospecto.
 *
 * `PAID` es el que pidió el negocio: pagaron, es cliente. Los otros dos van con
 * él por una razón concreta, no por completitud — aceptar una propuesta CREA UN
 * PROYECTO (create_project_from_estimate copia el client_id de la propuesta), y
 * un proyecto sin cliente no tiene dirección, ni ficha, ni a quién facturarle.
 * Así que si se llega a Aceptada o Completada sin pasar por Pagada, el cliente
 * tiene que existir igual.
 */
export const LEAD_PROPOSAL_WON_STATUSES: EstimateStatus[] = [
    EstimateStatus.ACCEPTED,
    EstimateStatus.COMPLETED,
    EstimateStatus.PAID,
];

/** ¿Esta propuesta sigue esperando a que su prospecto cierre? */
export function isLeadProposal(estimate: Pick<Estimate, 'sourceLeadId'>): boolean {
    return !!estimate.sourceLeadId;
}

/** ¿Ganar este estado obliga a que el prospecto ya sea cliente? */
export function isWonStatus(status: EstimateStatus): boolean {
    return LEAD_PROPOSAL_WON_STATUSES.includes(status);
}

/**
 * Propuestas vivas de un prospecto, de la más reciente a la más vieja.
 * Las usa el panel del prospecto para enseñar lo que ya se le cotizó.
 */
export function proposalsForLead(estimates: Estimate[], leadId: string): Estimate[] {
    return estimates
        .filter(e => e.sourceLeadId === leadId)
        .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
}

/**
 * Prospectos que hay que convertir a cliente ahora mismo, porque alguna de sus
 * propuestas ya llegó a un estado ganado.
 *
 * Devuelve ids únicos: un prospecto con tres propuestas aceptadas se convierte
 * una vez, no tres. Puro para poder probar los bordes sin base de datos — es la
 * única pieza que decide QUIÉN se convierte automáticamente.
 */
export function leadsAwaitingConversion(estimates: Estimate[]): string[] {
    const ids = new Set<string>();
    for (const e of estimates) {
        if (!e.sourceLeadId) continue;
        if (!isWonStatus(e.status)) continue;
        ids.add(e.sourceLeadId);
    }
    return [...ids];
}
